/**
 * Phase 3 — userTouchedSlots accounting for vote-doc submits.
 *
 * The engine writes votes for "passive" slots (auto-cast / auto-submit) and
 * Tier 2/3 needs to know which slots the user took deliberate action on so
 * it can preserve those choices on subsequent reevals. This helper computes
 * the union of:
 *   - the previously-recorded `userTouchedSlots`, and
 *   - any slot whose persisted-vs-submitted value differs in the current
 *     submission ("the user changed it").
 *
 * Submitting an unchanged auto-cast vote is NOT counted as touching it —
 * the persisted and submitted values match, so the slot is not added.
 */

/**
 * Compute the next userTouchedSlots union.
 *
 * @param {Object} params
 * @param {Object<string, string|null>} params.persistedVotes  current vote doc's votes map
 * @param {Object<string, string|null>} params.submittedVotes  the votes about to be written
 * @param {string[]}                    params.existingTouched current vote doc's userTouchedSlots
 * @returns {string[]} sorted, deduped union
 */
export function computeNextUserTouchedSlots({
  persistedVotes = {},
  submittedVotes = {},
  existingTouched = [],
}) {
  const allSlotIds = new Set([
    ...Object.keys(persistedVotes || {}),
    ...Object.keys(submittedVotes || {}),
  ]);
  const newlyTouched = [];
  for (const slotId of allSlotIds) {
    const before = persistedVotes?.[slotId] ?? null;
    const after = submittedVotes?.[slotId] ?? null;
    if (before !== after) newlyTouched.push(slotId);
  }
  const existing = Array.isArray(existingTouched) ? existingTouched : [];
  return Array.from(new Set([...existing, ...newlyTouched])).sort();
}
